/**
 * LiteratureArticleDrawer — the full PubMed record for one literature hit (86d3cefah).
 *
 * MedQuery stores NO article body — only the abstract, its structured sections, metadata and
 * references. So this view is the complete PubMed *record* plus prominent outbound links to
 * where the real full text actually lives: PMC (free, when pmc_id is present), the publisher
 * (via DOI), and PubMed. The copy says so plainly — it must never imply an inline body.
 *
 * Fetches on open via useLiteratureArticle(pmid). A not-found pmid and a degraded service are
 * both normal `available: false` envelopes, not errors. `enrichment_status`:
 *   'withdrawn'   → retraction/withdrawn warning (retracted records can still surface in search)
 *   'unavailable' → the extended panel is silently omitted; the document still renders in full
 *   'ok'          → the extended panel renders, guarding every loosely-typed field access.
 *
 * Width is the §16 standard 70vw, with a TWO-COLUMN body. The width and the legibility of the
 * abstract are not actually in tension: prose stays in a measure-capped main column (~720px —
 * a full-viewport line length is genuinely hard to read), and the width is spent on the
 * metadata sidebar instead of on longer lines. That keeps the drawer consistent with the rest
 * of the platform without stretching an abstract across a 1440px monitor.
 * All colour via --mg-* tokens.
 */

import React from 'react';
import { Typography, Button, Space, Tag, Alert, Grid } from 'antd';
import {
	ExportOutlined,
	FileTextOutlined,
	MessageOutlined,
	WarningOutlined,
} from '@ant-design/icons';

import { DetailDrawer, EmptyState } from '../../components/shared';
import { useLiteratureArticle } from '../../hooks/useResearch';
import type { AbstractSection, ResearchRecord } from '../../types/research/ResearchTypes';

const { Text, Paragraph, Link } = Typography;

/** Reading measure for the abstract. Beyond roughly this the eye loses the line. */
const PROSE_MAX_W = 720;
const META_COL_W = 300;

// ── Defensive readers (enrichment is a loosely-typed forwarded jsonb record) ─────

const asString = (v: unknown): string | null =>
	typeof v === 'string' && v.trim().length > 0 ? v : null;

const asNumber = (v: unknown): number | null =>
	typeof v === 'number' && Number.isFinite(v) ? v : null;

const asStringArray = (v: unknown): string[] =>
	Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

const asRecordArray = (v: unknown): Record<string, unknown>[] =>
	Array.isArray(v)
		? v.filter((x): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x))
		: [];

/** "RetractionIn" → "Retraction in" — sentence case (§23), not title case. */
const humaniseRefType = (v: string): string =>
	v
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		.toLowerCase()
		.replace(/^./, (c) => c.toUpperCase());

// ── Layout atoms ────────────────────────────────────────────────────────────────

/** A sidebar fact. `.mg-label` (§4) is the system's 10/600 uppercase heading for exactly this. */
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<div style={{ marginBottom: 14 }}>
		<Text className="mg-label" style={{ display: 'block', marginBottom: 4 }}>
			{label}
		</Text>
		<div style={{ fontSize: 'var(--mg-fs-md)', color: 'var(--mg-text)' }}>{children}</div>
	</div>
);

const TagList: React.FC<{ values: string[] }> = ({ values }) => {
	if (values.length === 0) return <Text type="secondary">—</Text>;
	return (
		<Space size={4} wrap>
			{values.map((v, i) => (
				<Tag key={`${v}-${i}`} style={{ fontSize: 'var(--mg-fs-xs)' }}>
					{v}
				</Tag>
			))}
		</Space>
	);
};

/** A labelled section of a structured abstract; falls back to the NLM category. */
const sectionHeading = (s: AbstractSection): string | null => asString(s.label) ?? asString(s.nlm_category);

// ── Props ───────────────────────────────────────────────────────────────────────

export interface LiteratureArticleDrawerProps {
	open: boolean;
	onClose: () => void;
	/** The clicked search hit — its meta.pmid drives the fetch. */
	record: ResearchRecord | null;
	/** Whether the Xoe handoff affordance is shown (mirrors VITE_XOE_ENABLED). */
	xoeEnabled?: boolean;
	/** Starts a Xoe conversation seeded with this record. */
	onDiscuss?: (record: ResearchRecord) => void;
	/** True while the Xoe conversation is being created/seeded. */
	discussing?: boolean;
}

// ── Component ───────────────────────────────────────────────────────────────────

const LiteratureArticleDrawer: React.FC<LiteratureArticleDrawerProps> = ({
	open,
	onClose,
	record,
	xoeEnabled = false,
	onDiscuss,
	discussing = false,
}) => {
	const pmid = record ? asString(record.meta?.pmid) : null;
	const { data, isLoading, isError } = useLiteratureArticle(pmid);
	const screens = Grid.useBreakpoint();
	/** Below lg the sidebar drops under the prose rather than squeezing both (§21). */
	const isWide = !!screens.lg;

	const footer =
		record && xoeEnabled && onDiscuss ? (
			<Button
				type="default"
				icon={<MessageOutlined />}
				loading={discussing}
				onClick={() => onDiscuss(record)}
				data-testid="research-discuss-xoe"
			>
				Discuss with Xoe
			</Button>
		) : undefined;

	if (!record) {
		return <DetailDrawer open={open} onClose={onClose} title="Article" width="70vw" children={null} />;
	}

	// ── Non-document states ───────────────────────────────────────────────────
	if (isError) {
		return (
			<DetailDrawer open={open} onClose={onClose} title={record.title} width="70vw">
				<EmptyState title="Could not load article" description="Please try again." />
			</DetailDrawer>
		);
	}

	if (data && data.available === false) {
		return (
			<DetailDrawer open={open} onClose={onClose} title={record.title} width="70vw">
				<EmptyState title="Article unavailable" description={data.message} />
			</DetailDrawer>
		);
	}

	const doc = data?.available ? data.document : null;
	const enrichment = data?.available ? data.enrichment : null;
	const enrichmentStatus = data?.available ? data.enrichment_status : null;

	// ── Header ────────────────────────────────────────────────────────────────
	const journal = doc ? asString(doc.journal) : null;
	const year = doc?.pub_year != null ? String(doc.pub_year) : null;
	const subtitle = [journal, year].filter(Boolean).join(' · ') || undefined;

	// ── Body ──────────────────────────────────────────────────────────────────
	// Two columns: the abstract reads left at a capped measure; everything you look up or click
	// through to sits right, the way PubMed's own record page is arranged. The retraction alert
	// spans both — it is the one thing that must be seen before anything else.

	/** The prose column: attribution, then the abstract. Nothing above the thing you came to read. */
	const prose = doc ? (
		<>
			{doc.authors.length > 0 && (
				<Text type="secondary" style={{ fontSize: 'var(--mg-fs-md)', display: 'block', marginBottom: 16 }}>
					{doc.authors.join(', ')}
				</Text>
			)}

			<Text className="mg-label" style={{ display: 'block', marginBottom: 8 }}>
				Abstract
			</Text>

			{/* Structured abstract when the sections are present; the flat string otherwise. */}
			{doc.abstract_sections.length > 0 ? (
				<div data-testid="research-article-abstract-sections">
					{doc.abstract_sections.map((s, i) => {
						const heading = sectionHeading(s);
						return (
							<div key={`${heading ?? 'section'}-${i}`} style={{ marginBottom: 14 }}>
								{heading && (
									<Text
										strong
										style={{
											fontSize: 'var(--mg-fs-xs)',
											textTransform: 'uppercase',
											letterSpacing: '0.04em',
											color: 'var(--mg-primary)',
											display: 'block',
											marginBottom: 2,
										}}
									>
										{heading}
									</Text>
								)}
								<Paragraph style={{ marginBottom: 0, fontSize: 'var(--mg-fs-md)', lineHeight: 1.6 }}>
									{s.text}
								</Paragraph>
							</div>
						);
					})}
				</div>
			) : doc.abstract ? (
				<Paragraph style={{ marginBottom: 0, fontSize: 'var(--mg-fs-md)', lineHeight: 1.6 }}>
					{doc.abstract}
				</Paragraph>
			) : (
				<Text type="secondary">No abstract available.</Text>
			)}
		</>
	) : null;

	/**
	 * Full-text links lead the sidebar: MedQuery holds no article body, so "go read it" is the
	 * primary action on this screen — but it is a destination, not the content, so it sits beside
	 * the abstract rather than on top of it.
	 */
	const fullText = doc ? (
		<div
			style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}
			data-testid="research-article-fulltext"
		>
			<Text className="mg-label" style={{ marginBottom: 2 }}>
				Full text
			</Text>
			{doc.links.pmc && (
				<Button
					type="primary"
					block
					icon={<FileTextOutlined />}
					href={doc.links.pmc}
					target="_blank"
					rel="noopener noreferrer"
					data-testid="research-article-pmc-link"
				>
					Free full text · PMC
				</Button>
			)}
			{doc.links.doi && (
				<Button
					block
					icon={<ExportOutlined />}
					href={doc.links.doi}
					target="_blank"
					rel="noopener noreferrer"
					data-testid="research-article-doi-link"
				>
					Publisher · DOI
				</Button>
			)}
			{doc.links.pubmed && (
				<Button
					block
					icon={<ExportOutlined />}
					href={doc.links.pubmed}
					target="_blank"
					rel="noopener noreferrer"
					data-testid="research-article-pubmed-link"
				>
					PubMed
				</Button>
			)}
			<Text type="secondary" style={{ fontSize: 'var(--mg-fs-xs)', marginTop: 2 }}>
				Abstract and metadata only — the article text is hosted externally.
			</Text>
		</div>
	) : null;

	/** Everything that is looked up or clicked rather than read — kept out of the prose column. */
	const metadata = doc ? (
		<>
			{fullText}

			<Field label="Identifiers">
				<Space size={8} wrap>
					{doc.pmid && <Tag style={{ fontSize: 'var(--mg-fs-xs)' }}>PMID: {doc.pmid}</Tag>}
					{doc.doi && <Tag style={{ fontSize: 'var(--mg-fs-xs)' }}>DOI: {doc.doi}</Tag>}
					{doc.pmc_id && <Tag style={{ fontSize: 'var(--mg-fs-xs)' }}>{doc.pmc_id}</Tag>}
					{!doc.pmid && !doc.doi && !doc.pmc_id && <Text type="secondary">—</Text>}
				</Space>
			</Field>

			<Field label="Publication types">
				<TagList values={doc.pub_types} />
			</Field>

			<Field label="MeSH terms">
				<TagList values={doc.mesh} />
			</Field>

			<Field label="Chemicals / substances">
				<TagList values={doc.chemicals} />
			</Field>

			<Field label="Keywords">
				<TagList values={doc.keywords} />
			</Field>

			{/* Extended record — only when the enrichment replica answered. 'unavailable' is a
			    normal state: omit this panel silently, never show an error. */}
			{enrichmentStatus === 'ok' && enrichment && <EnrichmentPanel enrichment={enrichment} />}
		</>
	) : null;

	const body = doc ? (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
			{/* Retraction / withdrawal — retracted records are soft-deleted upstream but can
			    still appear in search, so this must be unmissable: it spans both columns. */}
			{enrichmentStatus === 'withdrawn' && (
				<Alert
					type="warning"
					showIcon
					icon={<WarningOutlined />}
					message="Retracted or withdrawn"
					description="This article is no longer in the reference index — it may have been retracted or withdrawn. Check the PubMed record before relying on it."
					data-testid="research-article-withdrawn"
				/>
			)}

			<div
				style={{
					display: 'flex',
					gap: 24,
					alignItems: 'flex-start',
					...(isWide ? {} : { flexDirection: 'column' }),
				}}
			>
				{/* Prose — capped measure. This is the whole reason 70vw does not hurt here. */}
				<div
					style={{
						flex: 1,
						minWidth: 0,
						maxWidth: isWide ? PROSE_MAX_W : undefined,
					}}
				>
					{prose}
				</div>

				{/* Metadata sidebar */}
				<div
					style={{
						width: isWide ? META_COL_W : '100%',
						flexShrink: 0,
						...(isWide
							? { borderLeft: '1px solid var(--mg-border)', paddingLeft: 24 }
							: { borderTop: '1px solid var(--mg-border)', paddingTop: 12 }),
					}}
				>
					{metadata}
				</div>
			</div>
		</div>
	) : null;

	return (
		<DetailDrawer
			open={open}
			onClose={onClose}
			width="70vw"
			title={doc?.title || record.title}
			subtitle={subtitle}
			loading={isLoading}
			loadingMessage="Loading article…"
			footer={footer}
		>
			{body}
		</DetailDrawer>
	);
};

// ── Extended (enrichment) panel ─────────────────────────────────────────────────

/**
 * The optional long-tail record from the read-only pubmed replica. Every field is read
 * defensively — this is a forwarded jsonb payload, not a typed contract, so a missing or
 * unexpected value must degrade to "not shown", never throw.
 */
const EnrichmentPanel: React.FC<{ enrichment: Record<string, unknown> }> = ({ enrichment }) => {
	// Citation line — journal metadata Solr does not carry.
	const citation = [
		asString(enrichment.journal_iso) ?? asString(enrichment.journal_title),
		asString(enrichment.journal_volume) && `Vol ${asString(enrichment.journal_volume)}`,
		asString(enrichment.journal_issue) && `Issue ${asString(enrichment.journal_issue)}`,
		asString(enrichment.pagination),
	]
		.filter(Boolean)
		.join(' · ');

	const refsCount = asNumber(enrichment.refsCount);
	const languages = asStringArray(enrichment.languages);
	const corrections = asRecordArray(enrichment.comments_corrections);
	const country = asString(enrichment.country);

	// Nothing worth rendering — don't draw an empty panel.
	if (!citation && refsCount == null && languages.length === 0 && corrections.length === 0 && !country) {
		return null;
	}

	return (
		<div
			style={{
				borderTop: '1px solid var(--mg-border)',
				paddingTop: 12,
				display: 'flex',
				flexDirection: 'column',
			}}
			data-testid="research-article-enrichment"
		>
			{citation && <Field label="Citation">{citation}</Field>}
			{country && <Field label="Country of publication">{country}</Field>}
			{languages.length > 0 && (
				<Field label="Languages">
					<TagList values={languages} />
				</Field>
			)}
			{refsCount != null && (
				<Field label="References">
					{refsCount} reference{refsCount === 1 ? '' : 's'}
				</Field>
			)}
			{corrections.length > 0 && (
				<Field label="Comments & corrections">
					<div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
						{corrections.map((c, i) => {
							const refType = asString(c.ref_type);
							const refPmidNum = asNumber(c.pmid);
							const refPmid = asString(c.pmid) ?? (refPmidNum != null ? String(refPmidNum) : null);
							const note = asString(c.note);
							// A "RetractionIn"/"ErratumIn" notice is clinically material — mark it.
							const isNotice = !!refType && /retract|erratum/i.test(refType);
							return (
								<div key={`${refType ?? 'ref'}-${refPmid ?? i}`} style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
									{refType && (
										<Tag
											style={{
												fontSize: 'var(--mg-fs-xs)',
												...(isNotice
													? { background: 'var(--mg-warning-bg)', borderColor: 'var(--mg-warning)', color: 'var(--mg-warning)' }
													: {}),
											}}
										>
											{humaniseRefType(refType)}
										</Tag>
									)}
									{refPmid && (
										<Link
											href={`https://pubmed.ncbi.nlm.nih.gov/${refPmid}/`}
											target="_blank"
											rel="noopener noreferrer"
											style={{ fontSize: 'var(--mg-fs-xs)' }}
										>
											PMID {refPmid}
										</Link>
									)}
									{note && (
										<Text type="secondary" style={{ fontSize: 'var(--mg-fs-xs)' }}>
											{note}
										</Text>
									)}
								</div>
							);
						})}
					</div>
				</Field>
			)}
		</div>
	);
};

export default LiteratureArticleDrawer;
